// eg. /app/store/mymodule.ts
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'

import { ApiTaNt020 } from '~/endpoints/ApiTaNt020'
import { ApiTaNt030 } from '~/endpoints/ApiTaNt030'
import { ApiTaNt040 } from '~/endpoints/ApiTaNt040'
import { ApiTaLi050 } from '~/endpoints/ApiTaLi050'

// State types

interface NoticesNodeState {
  noticeId: number
  title: string
  deliveryDate: string
  unreadFlag: boolean
  importantFlag: boolean
}
export type NoticesState = NoticesNodeState[]

export type NoticeDetailState = {
  body: string
  questionnaireSymbol: string
  buttonImageUrl: string
} | null

export type UrlState = {
  url: string
} | null

// State action parameter
interface ActionParamRefreshNotices {
  offset: number
  limit: number
  coursePlan: string[]
}

interface ActionParamNoticeId {
  noticeId: number
}

interface ActionParamSymbol {
  symbol: string
}

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/Notices',
})
export default class Notices extends VuexModule {
  // State
  private notices_: NoticesState = []
  private isMoreNotice_: boolean = true
  private noticeDetail_: NoticeDetailState = null
  private url_: UrlState = null

  // Getter: notices
  get notices(): NoticesState {
    return this.notices_
  }

  get hasNotice(): boolean {
    return this.notices_ !== null && this.notices_.length > 0
  }

  get isMoreNotice(): boolean {
    return this.isMoreNotice_
  }

  // Getter: noticeDetail
  get noticeDetail(): NoticeDetailState {
    return this.noticeDetail_
  }

  // Getter: url
  get url(): UrlState {
    return this.url_
  }

  @Mutation
  clearNotices() {
    this.notices_ = []
    this.isMoreNotice_ = true
  }

  @Mutation
  updateNotices(newState: NoticesState) {
    this.notices_ = this.notices_.concat(newState)
  }

  @Mutation
  updateIsMoreNotice(flg: boolean) {
    this.isMoreNotice_ = flg
  }

  @Mutation
  updateNoticeDetail(newState: NoticeDetailState) {
    this.noticeDetail_ = newState
  }

  @Mutation
  updateNoticeUnreadFlag(noticeId: number) {
    const target = this.notices_.find(elem => elem.noticeId === noticeId)
    if (target) {
      target.unreadFlag = false
    }
  }

  @Mutation
  updateUrl(newState: UrlState) {
    this.url_ = newState
  }

  @Action
  clear() {
    this.context.commit('clearNotices')
    this.context.commit('updateNoticeDetail', null)
    this.context.commit('updateUrl', null)
  }

  @Action
  async refreshNotices(param: ActionParamRefreshNotices) {
    logger.trace('in: refreshNotices')
    const api = new ApiTaNt020.Api()

    this.context.commit('updateIsMoreNotice', false)

    // API: ApiTaNt020
    let hasError = true
    const result = await api.get<NoticesState>(
      {
        query: {
          offset: param.offset,
          limit: param.limit,
          coursePlan: param.coursePlan,
        },
      },
      // success
      (data, _) => {
        hasError = false
        return data.map((elem): NoticesNodeState => {
          return {
            noticeId: elem.noticeId,
            title: elem.title,
            deliveryDate: elem.deliveryDate,
            unreadFlag: !!elem.unreadFlag,
            importantFlag: !!elem.importantFlag,
          }
        })
      },
      // Error
      (data, status) => {
        hasError = true
        logger.error(status, data)
        return []
      }
    )

    if (hasError || !result) {
      this.context.commit('updateIsMoreNotice', true)
    } else {
      this.context.commit('updateIsMoreNotice', result.length === param.limit)
    }

    if (hasError && this.hasNotice) {
      // 追加のお知らせ読み込みに失敗した場合はエラー扱いにしない
      return
    }

    this.context.commit('updateNotices', result)
  }

  @Action
  async refreshNoticeDetail(param: ActionParamNoticeId) {
    logger.trace('in: refreshNoticeDetail')
    const api = new ApiTaNt030.Api()

    // API: ApiTaNt030
    const result = await api.get<NoticeDetailState | null>(
      {
        path: {
          noticeId: param.noticeId,
        },
      },
      // success
      (data, _) => {
        return {
          body: data.body,
          questionnaireSymbol: data.questionnaireSymbol,
          buttonImageUrl: data.buttonImageUrl,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        logger.error(data.error.messageId, data.error.message)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateNoticeDetail', result)
  }

  @Action
  async updateNoticeReads(param: ActionParamNoticeId) {
    logger.trace('in: refreshNoticeReads')
    const api = new ApiTaNt040.Api()

    // API: ApiTaNt040
    const result = await api.post<boolean>(
      {
        path: {
          noticeId: param.noticeId,
        },
      },
      // success
      (_, __) => {
        return true
      },
      // Error
      (_, __) => {
        return false
      }
    )
    if (result) {
      this.context.commit('updateNoticeUnreadFlag', param.noticeId)
    }
  }

  @Action
  async refreshUrl(param: ActionParamSymbol) {
    logger.trace('in: refreshUrl')
    const api = new ApiTaLi050.Api()

    // リフレッシュ完了までは初期値であるnullにする
    this.context.commit('updateUrl', null)

    // API: ApiTaLi050
    const result = await api.get<UrlState | null>(
      {
        query: {
          symbol: param.symbol,
        },
      },
      // success
      (data, _) => {
        return {
          url: data.url,
        }
      },
      // error
      (data, status) => {
        logger.error(status, data)
        logger.error(data.error.messageId, data.error.message)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateUrl', result)
  }

  @Action
  initUrl() {
    logger.trace('in: initUrl')
    this.context.commit('updateUrl', {
      url: '',
    })
  }

  @Action
  initNotices() {
    logger.trace('in: initNotices')
    this.context.commit('clearNotices')
  }
}
